import {
    useLayoutEffect,
    startTransition,
    useCallback,
    useEffect,
    useState,
    useMemo
} from "react";
import {
    useWindowDimensions,
    ScrollView,
    StyleSheet,
    View
} from "react-native";
import {
    generatePalette
} from "./generator";
import {
    usePreviewText
} from "./previewText";
import PreviewWall from "./previewWall";
import {
    type CreatePreviewMessage,
    type CreateReadingSize,
    CREATE_MESSAGE_SOURCE,
    CREATE_PALETTE_NAME,
    type CreateSettings,
    type CreatePalette
} from "./settings";
import {
    decodeSettings
} from "./url";
import {
    type MarkdownVariantsType,
    NCoreUIKitTheme,
    MarkdownViewer,
    Avatar,
    Badge,
    Text
} from "ncore-ui-kit";
import {
    findBlock
} from "../blocks";

const stylesheet = StyleSheet.create({
    bylineText: {
        flexDirection: "column",
        flexShrink: 1
    },
    article: {
        flexDirection: "column",
        width: "100%"
    },
    reading: {
        alignItems: "center",
        width: "100%"
    },
    byline: {
        flexDirection: "row",
        alignItems: "center"
    },
    category: {
        flexDirection: "row"
    },
    content: {
        flexGrow: 1
    }
});

const handleLinkPress = () => undefined;

const getReadingVariants = (readingSize: CreateReadingSize): MarkdownVariantsType => ({
    "<br/>": readingSize,
    "<p>": readingSize,
    "1. ": readingSize,
    "- ": readingSize,
    "* ": readingSize,
    "+ ": readingSize,
    "<<": readingSize,
    "> ": readingSize,
    "": readingSize
});

const PreviewReading = ({
    readingSize
}: {
    readingSize: CreateReadingSize;
}) => {
    const t = usePreviewText();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const variants = useMemo(() => getReadingVariants(readingSize), [
        readingSize
    ]);

    const isCompact = width < shapeConfigs.compactBreakpoint;

    return <View
        style={[
            stylesheet.reading,
            {
                paddingVertical: isCompact ? spaces.spacingLg : spaces.spacingXxl,
                paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg
            }
        ]}
    >
        <View
            style={[
                stylesheet.article,
                {
                    maxWidth: shapeConfigs.readingMaxWidth,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <View
                style={stylesheet.category}
            >
                <Badge
                    title={t("createPreview.reading.category")}
                    variant="outline"
                    type="neutral"
                />
            </View>
            <View
                style={[
                    stylesheet.byline,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Avatar
                    title={t("createPreview.reading.author")}
                    size="small"
                />
                <View
                    style={stylesheet.bylineText}
                >
                    <Text
                        variant="labelMediumSize"
                        color="high"
                    >
                        {t("createPreview.reading.author")}
                    </Text>
                    <Text
                        variant="bodySmallSize"
                        color="low"
                    >
                        {t("createPreview.reading.meta")}
                    </Text>
                </View>
            </View>
            <MarkdownViewer
                content={t("createPreview.reading.article")}
                onLinkPress={handleLinkPress}
                variants={variants}
                isReading={true}
            />
        </View>
    </View>;
};

const CreatePreview = () => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        height
    } = useWindowDimensions();

    const [
        settings,
        setSettings
    ] = useState<CreateSettings>(() => decodeSettings(window.location.search));

    const applyPreview = useCallback((nextSettings: CreateSettings, palette: CreatePalette) => {
        NCoreUIKitTheme.updateProjectTheme({
            palettes: [
                {
                    themes: palette.themes as NCoreUIKit.PartialTheme,
                    name: CREATE_PALETTE_NAME
                }
            ],
            shapes: {
                shapeConfigs: {
                    cornerShape: nextSettings.cornerShape
                }
            }
        }, {
            activePalette: CREATE_PALETTE_NAME as keyof NCoreUIKit.PaletteKey,
            activeGapPropagation: nextSettings.density,
            activeSharpness: nextSettings.sharpness,
            activeTheme: nextSettings.themeKey
        });

        setSettings(nextSettings);
    }, []);

    useLayoutEffect(() => {
        applyPreview(settings, generatePalette(settings));
    }, []);

    useEffect(() => {
        const handleMessage = (event: MessageEvent<Partial<CreatePreviewMessage> | undefined>) => {
            if(event.origin !== window.location.origin || event.data?.source !== CREATE_MESSAGE_SOURCE) {
                return;
            }

            const {
                settings: nextSettings,
                palette
            } = event.data;

            if(!nextSettings || !palette) {
                return;
            }

            startTransition(() => {
                applyPreview(nextSettings, palette);
            });
        };

        window.addEventListener("message", handleMessage);

        if(window.parent !== window) {
            window.parent.postMessage({
                source: CREATE_MESSAGE_SOURCE
            }, window.location.origin);
        }

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [
        applyPreview
    ]);

    useLayoutEffect(() => {
        document.documentElement.style.backgroundColor = colors.content.container.default;

        document.body.style.backgroundColor = colors.content.container.default;

        document.body.style.colorScheme = settings.themeKey;
    }, [
        settings.themeKey,
        colors
    ]);

    const renderContent = () => {
        if(settings.preview === "components") {
            return <PreviewWall/>;
        }

        if(settings.preview === "reading") {
            return <PreviewReading
                readingSize={settings.readingSize}
            />;
        }

        const block = findBlock(settings.preview);

        if(!block) {
            return null;
        }

        const BlockComponent = block.Component;

        return <BlockComponent/>;
    };

    return <ScrollView
        style={{
            backgroundColor: colors.content.container.default,
            height
        }}
        contentContainerStyle={stylesheet.content}
        key={settings.preview}
    >
        {renderContent()}
    </ScrollView>;
};
export default CreatePreview;
